import { connectRouter } from "connected-react-router";
import { History } from "history";
import { combineReducers } from "redux";
import { StateType } from "typesafe-actions";

import component, { ComponentActions } from "../component/componentReducer";
import scene, { SceneActions } from "../scene/sceneReducer";

import accessChange, { AccessChangeActions } from "./accessChange";
import activity, { ActivityActions } from "./activity";
import announcements, { AnnouncementsActions } from "./announcements";
import api, { ApiActions } from "./api";
import app, { AppActions } from "./app";
import auth, { AuthActions } from "./auth";
import boundaryLegends, { BoundaryLegendActions } from "./boundaryLegends";
import boundaryLegendsUi, {
  BoundaryLegendsActions as BoundaryLegendsUiActions,
} from "./boundaryLegendsUi";
import chat, { ChatActions } from "./chat";
import { CompanyActions } from "./company";
import cumul, { CumulActions } from "./cumul";
import customFields, { CustomFieldsActions } from "./customFields";
import deal, { DealActions } from "./deal";
import dealLossReasons, { DealLossReasonsActions } from "./dealLossReasons";
import email, { EmailActions } from "./email";
import entityView, { EntityViewActions } from "./entityView";
import { ErrorActions } from "./errors";
import fieldInsights, { FieldInsightsActions } from "./fieldInsights";
import files, { FilesActions } from "./files";
import fileTags, { FileTagsActions } from "./fileTags";
import frequency, { FrequencyActions } from "./frequency";
import globalSearch, { GlobalSearchActions } from "./globalSearch";
import groups, { GroupsActions } from "./groups";
import iam, { IamActions } from "./iam";
import integrations, { IntegrationActions } from "./integrations";
import layout, { LayoutActions } from "./layout";
import locale, { LocaleActions } from "./locale";
import map, { MapActions } from "./map";
import mapLayers, { MapLayersActions } from "./mapLayers";
import mapLegends, { MapLegendsActions } from "./mapLegends";
import members, { MembersActions } from "./members";
import notification, { NotificationsActions } from "./notification";
import nylas, { NylasActions } from "./nylas";
import onboarding, { OnboardingActions } from "./onboarding";
import { PersonActions } from "./person";
import pinLegends, { PinLegendActions } from "./pinLegends";
import pinSettingsUi, { PinSettingsUiActions } from "./pinSettingsUi";
import recordPreview, { RecordPreviewActions } from "./recordPreview";
import referenceData, { ReferenceDataActions } from "./referenceData";
import { ResourcesActions } from "./resources";
import routes, { RoutesActions } from "./routes";
import savedFilters, { SavedFiltersActions } from "./savedFilters";
import savedLocations, { SavedLocationsActions } from "./savedLocations";
import savedLocationsUi, { SavedLocationsUiActions } from "./savedLocationsUi";
import schema, { SchemaActions } from "./schema";
import stackRank, { StackRankActions } from "./stackRank";
import territories, { TerritoriesActions } from "./territories";
import theme, { ThemeActions } from "./theme";
import uiSync, { UiSyncActions } from "./uiSync";
import userColor, { UserColorActions } from "./userColor";

const createRootReducer = (history: History) =>
  combineReducers({
    accessChange,
    activity,
    announcements,
    api,
    app,
    auth,
    boundaryLegends,
    boundaryLegendsUi,
    chat,
    component,
    cumul,
    customFields,
    deal,
    dealLossReasons,
    email,
    entityView,
    fieldInsights,
    files,
    fileTags,
    frequency,
    globalSearch,
    groups,
    iam,
    integrations,
    layout,
    locale,
    map,
    mapLayers,
    mapLegends,
    members,
    notification,
    nylas,
    onboarding,
    pinLegends,
    pinSettingsUi,
    recordPreview,
    referenceData,
    router: connectRouter(history),
    routes,
    savedFilters,
    savedLocations,
    savedLocationsUi,
    scene,
    schema,
    stackRank,
    territories,
    theme,
    uiSync,
    userColor,
  });

export type Actions =
  | AccessChangeActions
  | ActivityActions
  | AnnouncementsActions
  | ApiActions
  | AppActions
  | AuthActions
  | BoundaryLegendActions
  | BoundaryLegendsUiActions
  | ChatActions
  | CompanyActions
  | ComponentActions
  | CumulActions
  | CustomFieldsActions
  | DealActions
  | DealLossReasonsActions
  | EmailActions
  | EntityViewActions
  | ErrorActions
  | FieldInsightsActions
  | FilesActions
  | FileTagsActions
  | FrequencyActions
  | GlobalSearchActions
  | GroupsActions
  | IamActions
  | IntegrationActions
  | LayoutActions
  | LocaleActions
  | MapActions
  | MapLayersActions
  | MapLegendsActions
  | MembersActions
  | NotificationsActions
  | NylasActions
  | OnboardingActions
  | PersonActions
  | PinLegendActions
  | PinSettingsUiActions
  | RecordPreviewActions
  | ReferenceDataActions
  | ResourcesActions
  | RoutesActions
  | SavedFiltersActions
  | SavedLocationsActions
  | SavedLocationsUiActions
  | SceneActions
  | SchemaActions
  | StackRankActions
  | TerritoriesActions
  | ThemeActions
  | UiSyncActions
  | UserColorActions;

declare module "typesafe-actions" {
  interface Types {
    RootAction: Actions;
  }
}

export type ActionsTypes = Actions["type"];

export type RootState = StateType<ReturnType<typeof createRootReducer>>;

export default createRootReducer;
